import React, { useCallback, useEffect, useRef } from 'react';
import { useRouter } from '@funtech-int/rival-react';
import { TvChannel } from '@funtech-int/kp-graphql-sdk/yango-movies-www';
import { useAnalytics, useFeatureFlags } from '@funtech-int/yango-shared';

import { useModal } from '@/shared/hooks/usePopups';
import { QueryParameter } from '@/shared/constants/queryParams';
import { removeQueryParamsFromUrl } from '@/shared/lib/navigation';
import { startPlaying } from '@/scenarios/legacy-player/components/player-manager/helpers/startPlaying';
import { PlayerContentType } from '@/scenarios/legacy-player/components/feature-player/types/content';

import { featureFlagsScheme } from '../lib/featureFlags';
import ChannelCardModalContainer from '../components/ChannelCardModal/ChannelCardModal';
import styles from '../components/ChannelCardModal/ChannelCardModal.module.css';
import { createAnalyticsEvents } from '../lib/createAnalyticsEvents';

import { useChannelsWallpapersManager } from './useChannelsWallpapersManager';

export function useChannelCardModal() {
    const modal = useModal();
    const router = useRouter();
    const { flags } = useFeatureFlags(featureFlagsScheme);
    const isNewPlayerEnabled = flags.get('isNewPlayerEnabled');
    const wallpapperChannelsManager = useChannelsWallpapersManager();
    const analytics = useAnalytics(createAnalyticsEvents);
    const openedModalId = useRef<null | number>(null);

    const open = useCallback(
        (channel: TvChannel) => {
            const close = () => {
                analytics.navigatedToChannelList();
                removeQueryParamsFromUrl({
                    router,
                    queryParams: [QueryParameter.OPENED_CHANNEL_ID],
                });
            };

            const onCloseAfterSuccessPayment = () => {
                startPlaying({
                    router,
                    contentId: channel.contentId,
                    contentType: PlayerContentType.CHANNEL,
                    isNewPlayerEnabled,
                });
                return openedModalId.current && modal.hide(openedModalId.current);
            };

            openedModalId.current = modal.show({
                onClose: close,
                className: styles.root,
                closeButtonClassName: styles.modalCloseButton,
                content: (
                    <ChannelCardModalContainer
                        channel={channel}
                        wallpapperChannelsManager={wallpapperChannelsManager}
                        onCloseAfterSuccessPayment={onCloseAfterSuccessPayment}
                    />
                ),
            });
        },
        [modal, router, isNewPlayerEnabled, wallpapperChannelsManager, analytics]
    );

    useEffect(
        () => () => {
            if (openedModalId.current) {
                modal.hide(openedModalId.current);
            }
        },
        [modal]
    );

    return { open };
}
